import { useState } from 'react'

import type { AxiosError } from 'axios'

import { captureException as sentryCaptureException, setUser, type SeverityLevel, withScope } from '@sentry/react'
import type { Contexts, Primitive } from '@sentry/core'

export function ifAxiosErrorAddContext(eventContexts: Contexts | undefined, exception: AxiosError) {
	if (exception.isAxiosError) {
		const contexts = {
			...eventContexts
		}

		if (exception.response) {
			// The request was made and the server responded with a status code
			// that falls out of the range of 2xx
			contexts.errorResponse = {
				data: exception.response.data,
				status: exception.response.status,
				headers: exception.response.headers
			}
		} else if (exception.request) {
			// The request was made but no response was received
			// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
			// http.ClientRequest in node.js
			contexts.errorResponse = {
				request: exception.request
			}
		} else {
			// Something happened in setting up the request that triggered an Error
			contexts.errorResponse = {
				error: exception.message
			}
		}

		// We add the config anyway for info
		contexts.errorResponse = {
			...contexts.errorResponse,
			config: exception.config
		}

		return contexts
	}

	// If it's not axiosError we return original context
	return eventContexts
}

type SentryTag = Record<string, Primitive>

export function useSentry() {
	const [nodeEnv, setNodeEnv] = useState<'development' | 'production'>('development')

	function captureException(error: unknown, level: SeverityLevel = 'error', tags?: SentryTag | undefined) {
		if (nodeEnv === 'development') {
			console.error(error)
		}

		withScope(scope => {
			if (tags) {
				scope.setTags(tags)
			}

			scope.setLevel(level)
			sentryCaptureException(error)
		})
	}

	return {
		captureException,
		setUser,
		setNodeEnv
	}
}
